import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  InputAdornment,
  Divider,
  Snackbar,
  Popper,
  Paper,
  ClickAwayListener,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import wavesImage from "../../assets/img/waves.jpg";
import wavesImage2 from "../../assets/img/bg2.svg";
import wavesImage3 from "../../assets/img/bg33.svg";
import { CiLocationOn } from "react-icons/ci";
import locationIcon from "../../assets/img/locationIcon.svg";
import { COLORS } from "colors/color";
import { useTranslation } from "react-i18next"; // Import useTranslation

const SearchButton = styled("button")(({ theme, isMobile }) => ({
  backgroundColor: "#0085FF",
  color: "white",
  border: "none",
  cursor: "pointer",
  transition: "all 0.3s ease",
  fontFamily: "Poppins",
  fontSize: "1.6rem",
  fontWeight: 500,
  whiteSpace: "nowrap",
  width: isMobile ? "100%" : "352px",
  height: "100%",
  padding: isMobile ? "16px" : "19px 32px",
  borderRadius: isMobile ? "8px" : "100px",
  marginTop: isMobile ? "12px" : "0",
  background: isMobile
    ? "linear-gradient(135deg, #0085FF 0%, #00B2FF 100%)"
    : "#0085FF",
  boxShadow: isMobile ? "0 4px 15px rgba(0, 133, 255, 0.3)" : "none",
  transform: "translateY(0)",
  "&:hover": {
    transform: isMobile ? "translateY(-2px)" : "none",
    boxShadow: isMobile
      ? "0 6px 20px rgba(0, 133, 255, 0.4)"
      : "0 4px 12px rgba(0, 102, 255, 0.3)",
  },
}));

const SearchContainer = styled(Box)(({ theme, isMobile, isTab }) => ({
  minHeight: "600px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingTop: theme.spacing(15),
  position: "relative",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundImage: `url(${wavesImage})`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
  },
  [theme.breakpoints.up("md")]: {
    // backgroundImage: `url(${wavesImage})`, // Desktop (md and up)
  },
  [theme.breakpoints.between(480, "md")]: {
    // backgroundImage: `url(${wavesImage2})`, // Tablets (sm to md)
  },
  [theme.breakpoints.down("sm")]: {
    // backgroundImage: `url(${wavesImage3})`, // Mobile (below sm)
    paddingTop: theme.spacing(10),
  },
}));

const SearchBox = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  backgroundColor: "#FFFFFFF2",
  borderRadius: isMobile ? "12px" : "50px",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  width: "650px",
  maxWidth: isMobile ? "90%" : "80%",
  margin: "20px auto 20px",
  position: isMobile ? "relative" : "absolute",
  top: isMobile ? "auto" : "69%",
  left: isMobile ? "auto" : "50%",
  transform: isMobile ? "translate(0%, -10%)" : "translate(-50%, -50%)",
  zIndex: 2,
  flexDirection: isMobile ? "column" : "row",
  padding: isMobile ? "16px" : "8px",
  gap: isMobile ? "12px" : "8px",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  fontFamily: "Poppins",
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    borderRadius: 0,
    "& fieldset": {
      borderColor: "transparent",
      borderRadius: 0,
    },
    "&:hover fieldset": {
      borderColor: "transparent",
    },
    "&.Mui-focused fieldset": {
      borderColor: "transparent",
    },
  },
  "& .MuiInputBase-input": {
    color: "#000001aa",
    fontFamily: "Poppins",
    fontSize: "18px",
    [theme.breakpoints.down("md")]: {
      fontSize: "1.3rem",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
    },
  },
}));

const JobCountBanner = styled(Box)(({ theme, isMobile }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  height: 104,
  background: "linear-gradient(90deg, #007EF2 0%, #ABD7FF 100%)",
  color: "white",
  padding: theme.spacing(2),
  paddingX: theme.spacing(5),
  textAlign: "center",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  bottom: isMobile ? -78 : -59,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
    padding: theme.spacing(1),
  },
  [theme.breakpoints.down("xs")]: {
    fontSize: "0.8rem",
    padding: theme.spacing(0.5),
    height: 80,
  },
}));

const HeadingText = styled(Typography)(({ theme }) => ({
  fontFamily: "Kontora",
  position: "relative",
  zIndex: 1,
  fontWeight: 800,
  lineHeight: "45.8px",
  letterSpacing: "0.1rem",
  textAlign: "center",
  padding: "0.5rem 1rem",
  borderRadius: "8px",
  transition: "all 0.3s ease",
  [theme.breakpoints.up("md")]: {
    fontSize: "48px",
    padding: "0rem 0.75rem",
    background: `linear-gradient(100deg, ${COLORS.button_color}  5%, #fff 45%, #fff 90%, ${COLORS.button_color}  100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  [theme.breakpoints.up("xl")]: {
    lineHeight: "65.8px",
    fontSize: "63px",
    padding: "0.35rem 0.75rem",
    background: `linear-gradient(100deg, ${COLORS.button_color}  5%, #fff 39%, #fff 95%, ${COLORS.button_color}  100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  [theme.breakpoints.between("sm", "md")]: {
    fontSize: "28px",
    padding: "0rem 0.75rem",
    background: `linear-gradient(120deg, ${COLORS.button_color}  5%, #fff 35%, #fff 85%, ${COLORS.button_color}  100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
    padding: "0.25rem 0.5rem",
    background: `linear-gradient(120deg, ${COLORS.button_color}  5%, #fff 46%, #fff 85%, ${COLORS.button_color}  100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  color: "black",
  maxWidth: "90rem",
  margin: "0 auto",
  textAlign: "center",
  position: "relative",
  fontFamily: "Poppins",
  zIndex: 1,
  fontSize: "1.4rem",
  fontWeight: 500,
  textTransform: "none",
  [theme.breakpoints.down("md")]: {
    fontSize: "1.2rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
    maxWidth: "100%",
  },
  [theme.breakpoints.down("xs")]: {
    fontSize: "0.875rem",
  },
}));

const SearchBar = ({ props }) => {
  const { t } = useTranslation(); // Use the translation hook
  const apiUrl = process.env.REACT_APP_API_URL;
  const searchInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [location, setLocation] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTab = useMediaQuery(theme.breakpoints.down("mdd"));

  const [suggestions, setSuggestions] = useState({
    titles: [],
    locations: [],
  });
  const [anchorEl, setAnchorEl] = useState(null);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  useEffect(() => {
    console.log("isMobile", isMobile);
  }, [isMobile]);

  const fetchSuggestions = async (query, type) => {
    if (query.trim().length < 2) {
      setSuggestions({ titles: [], locations: [] });
      return;
    }
    try {
      const url = `${apiUrl}posts/autocomplete/?query=${encodeURIComponent(
        query
      )}&type=${type}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (type === "title") {
        setSuggestions((prev) => ({ ...prev, titles: data.suggestions }));
      } else {
        setSuggestions((prev) => ({ ...prev, locations: data.suggestions }));
      }
    } catch (error) {
      console.error(`Error fetching ${type} suggestions:`, error);
    }
  };

  const debouncedTitleSuggestions = debounce(
    (query) => fetchSuggestions(query, "title"),
    300
  );
  const debouncedLocationSuggestions = debounce(
    (query) => fetchSuggestions(query, "localisation"),
    300
  );

  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedTitleSuggestions(value);

    if (value.trim().length >= 2) {
      setAnchorEl(searchInputRef.current);
    } else {
      setAnchorEl(null);
    }
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    debouncedLocationSuggestions(value);

    if (value.trim().length >= 2) {
      setAnchorEl(locationInputRef.current);
    } else {
      setAnchorEl(null);
    }
  };

  const handleSuggestionSelect = (suggestion, type) => {
    if (type === "title") {
      setSearchTerm(suggestion);
    } else {
      setLocation(suggestion);
    }
    setAnchorEl(null);
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const handleSearch = async (event) => {
    event.preventDefault();
    if (searchTerm.trim() === "" && location.trim() === "") {
      showSnackbar(t("searchBox.errors.enter_job_or_city"), "error"); // Translated error message
      return;
    }

    try {
      let url = `${apiUrl}posts/search_recommendation/?`;
      if (searchTerm.trim()) {
        url += `title=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (location.trim()) {
        url += `${
          url.includes("?") ? "&" : "?"
        }localisation=${encodeURIComponent(location.trim())}`;
      }
      console.log(url);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.log("test12", data);

      if (data.posts && data.posts.length === 0) {
        props([], data.message);
      } else {
        props(data.posts, "");
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  };

  return (
    <SearchContainer isMobile={isMobile} isTab={isTab}>
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: "center",
            pt: { xs: 5, md: 5 },
            mb: { md: 8, xs: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          <HeadingText sx={{ mb: 0, mt: 2, whiteSpace: 'pre-line'  }}>
            {t("searchBox.heading")} {/* Translated heading */}
          </HeadingText>
          <SubtitleText
            sx={{
              mt: 2,
              mb: { md: 25, xs: 5 },
              fontSize: { md: "24px", xs: "13px" },
            }}
          >
            {t("searchBox.subtitle")} {/* Translated subtitle */}
          </SubtitleText>
        </Box>

        <form onSubmit={handleSearch}>
          <SearchBox isMobile={isMobile}>
            <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
              <div style={{ position: "relative", width: "100%" }}>
                <StyledTextField
                  sx={{
                    width: 280,
                  }}
                  ref={searchInputRef}
                  fullWidth
                  placeholder={t("searchBox.placeholder.job_title")} // Translated placeholder
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                />
                <Popper
                  open={Boolean(anchorEl) && suggestions.titles.length > 0}
                  anchorEl={anchorEl}
                  placement="bottom-start"
                  style={{ zIndex: 1300, width: anchorEl?.offsetWidth }}
                >
                  <Paper
                    elevation={3}
                    sx={{ maxHeight: 300, overflowY: "auto" }}
                  >
                    {suggestions.titles.map((suggestion, index) => (
                      <Typography
                        key={index}
                        onClick={() =>
                          handleSuggestionSelect(suggestion, "title")
                        }
                        sx={{
                          p: 1,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.05)",
                          },
                        }}
                      >
                        {suggestion}
                      </Typography>
                    ))}
                  </Paper>
                </Popper>
              </div>
            </ClickAwayListener>

            {!isMobile && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  backgroundColor: "#00000066",
                  marginY: 1,
                }}
              />
            )}

            <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
              <div style={{ position: "relative", width: "100%" }}>
                <StyledTextField
                  ref={locationInputRef}
                  fullWidth
                  placeholder={t("searchBox.placeholder.city_zip")} // Translated placeholder
                  value={location}
                  onChange={handleLocationChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 1 }}>
                        <img
                          src={locationIcon}
                          alt="Message Icon"
                          style={{ width: "24px", height: "24px", color: "#00000066" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Popper
                  open={Boolean(anchorEl) && suggestions.locations.length > 0}
                  anchorEl={anchorEl}
                  placement="bottom-start"
                  style={{ zIndex: 1300, width: anchorEl?.offsetWidth }}
                >
                  <Paper
                    elevation={3}
                    sx={{ maxHeight: 300, overflowY: "auto" }}
                  >
                    {suggestions.locations.map((suggestion, index) => (
                      <Typography
                        key={index}
                        onClick={() =>
                          handleSuggestionSelect(suggestion, "localisation")
                        }
                        sx={{
                          p: 1,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.05)",
                          },
                        }}
                      >
                        {suggestion}
                      </Typography>
                    ))}
                  </Paper>
                </Popper>
              </div>
            </ClickAwayListener>
            <SearchButton isMobile={isMobile} type="submit">
              {t("searchBox.find_jobs")} {/* Translated button text */}
            </SearchButton>
          </SearchBox>
        </form>
      </Container>

      <JobCountBanner isMobile={isMobile}>
        <Typography
          sx={{
            fontSize: { md: "32px", xs: 16 },
            fontFamily: "kontora",
            fontWeight: "100px",
          }}
        >
          {t("searchBox.job_count_banner")} {/* Translated banner text */}
        </Typography>
      </JobCountBanner>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        message={snackbar.message}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            color: "black",
            borderRadius: "50px",
            backdropFilter: "blur(10px)",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.4)",
          },
        }}
      />
    </SearchContainer>
  );
};

export default SearchBar;